import React, { forwardRef, useEffect, useState, } from "react";
import {
    Autocomplete, Button, Card, Checkbox, CircularProgress, createFilterOptions, Dialog, DialogContent, DialogTitle,
    Divider, FormControlLabel, FormGroup, Grid, IconButton, Slide, TextField, Typography, useTheme,
} from "@mui/material";
import { connect } from "react-redux";
//--------------------------|| 3RD PARTY LIBRARIES ||------------------------------------//
import {
    IconFileText,
    IconX,
    IconInputCheck,
    IconGeometry,
    IconMinusVertical,
    IconDatabase
} from "@tabler/icons-react";
//--------------------------|| FILES IMPORT ||------------------------------------------//
import {
    clearFormFields,
    clearFormulaDetails,
    getFormfields,
    formulaEdit,
    formulaEditErrorClear
} from "store/app/report-management/reports/actions";
import { DATA_TYPE_OPTIONS, METRICS_OPTIONS } from "store/app/report-management/reports/constants";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import { validateExpression } from "helper/math";
import { Required } from "ui-component/custom-components/mandatory";
import Chip from "ui-component/extended/Chip";

//--------------------------|| CUSTOM VARIABLES ||------------------------------------------//
const filter = createFilterOptions();
const headerStyles = { display: "flex", alignItems: "center", gap: "5px" };
const Transition = forwardRef((props, ref) => <Slide direction="left" ref={ref} {...props} />);

const ReportEdit = (props) => {

    const { open, onClose, formFields, formFieldsCount, filters, paginationData, errorMessage, isPending, formulaDetails, parent } = props;
    const { fromName } = parent || {};
    const theme = useTheme();
    const [fieldsData, setFieldsData] = useState({});
    const [selectAll, setSelectAll] = useState(false);
    const [fieldSelectOption, setFieldSelectOption] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [selectedDataType, setSelectedDataType] = useState({ label: "", value: null });
    const [selectedFormula, setSelectedFormula] = useState({ label: "", value: null });
    const [hint, setHint] = useState();
    const [isCustom, setIsCustom] = useState(false);
    const [reportName, setReportName] = useState();
    const [errors, setErrors] = useState();

    const dataTypeNamingConverter = (name) => {
        switch (name?.toLowerCase()) {
            case "int":
                return "Number";
            case "yes/no":
                return "Present/Absent";
            default:
                return name;
        }
    }

    useEffect(() => {
        if (formulaDetails && (Object.keys(formulaDetails).length > 0)) {
            const { reportName, dataType, metrics, fields, isCustomFormula, customFormula } = formulaDetails || {};
            //----------Update Formula Name------------
            setReportName(reportName);

            //----------Update Data Type------------
            const dataTypeOption = { label: dataTypeNamingConverter(dataType), value: dataType }
            setSelectedDataType(dataTypeOption);
            props.getFormfields(dataType);

            //----------Update Metric------------
            const metricOption = { label: metrics, value: metrics }
            setSelectedFormula(metricOption);

            //----------Update Field Selection------------
            let selectedField = {};
            fields?.map((field) => {
                return selectedField = {
                    ...selectedField,
                    [field.fieldName]: true,
                }
            });
            setFieldsData(selectedField);

            //----------Update Custom Formula------------
            setIsCustom(isCustomFormula);
            if (isCustomFormula) {
                const formula = customFormula?.split(" ");
                const formulaArray = formula?.map((ops) => {
                    const fieldArr = ops.includes("FRMFLD") ? fields?.filter((field) => field.fieldId === ops) : [ops];
                    const field = fieldArr[0]?.fieldName || fieldArr[0];
                    return {
                        label: field || ops,
                        value: ops
                    };
                })
                setSelectedOptions(formulaArray);
                setHint(formulaArray);
            }
            else {
                setSelectedOptions([]);
                setHint();
            }
        }
        setErrors();
        props.formulaEditErrorClear();
    }, [open])

    //----|| Report Name ||----//
    const handleReportName = (e) => {
        const { value } = e.target || {};
        setReportName(value);
        setErrors({
            ...errors,
            reportName: null,
        })
    }

    //----|| Data Type ||----//
    const handleDataTypeSelect = (e, option) => {
        const { value } = option || {};
        setSelectedDataType(option);
        setSelectAll(false);
        setSelectedFormula();
        setIsCustom(false);
        setFieldsData({});
        setErrors({
            ...errors,
            dataType: null,
        })
        if (value) {
            props.getFormfields(value);
        }
        else {
            props.clearFormFields();
        }
    }

    //----|| Fields Selection ||----//
    const handleSelectAll = (e) => {
        const { checked } = e.target || {};
        setSelectAll(checked);
        setSelectedOptions([]);
        setHint();
        setErrors({
            ...errors,
            fieldSelect: null,
        })
        if (checked) {
            let selectAllField = {};
            formFields?.map((field) => {
                return selectAllField = {
                    ...selectAllField,
                    [field.FieldName]: true,
                }
            });
            setFieldsData(selectAllField);
        }
        else {
            setFieldsData({});
        }
    }

    const handleSelectField = (e) => {
        const { checked, name } = e.target || {};
        setErrors({
            ...errors,
            fieldSelect: null,
        })
        if (checked) {
            setFieldsData({
                ...fieldsData,
                [name]: checked,
            })
        }
        else {
            const fields = { ...fieldsData };
            delete fields[name];
            setFieldsData(fields);
            setSelectedOptions([]);
            setHint();
        }
    }

    useEffect(() => {
        if (selectedDataType?.value) {
            const formulaOptions = formFields?.map((i) => {
                return {
                    label: i.FieldName,
                    value: i.Id
                }
            });
            const selectedKey = Object.keys(fieldsData);
            const selectedFields = selectedKey?.flatMap((key) =>
                formulaOptions?.filter((option) =>
                    option.label === key
                ))
            setFieldSelectOption(selectedFields);
            const isSelectedAll = formFieldsCount !== 0 ? formFieldsCount === selectedKey?.length : false;
            setSelectAll(isSelectedAll);
        }
    }, [fieldsData, formFieldsCount])

    //----|| Metrics ||----//
    const handleSelectFormula = (e, option) => {
        const { label } = option || {};
        const customFlag = label === "Custom";
        setIsCustom(customFlag);
        setSelectedFormula(option);
        setErrors({
            ...errors,
            metrics: null,
        })
    }

    const handleFormulaSelect = (e, option) => {
        const updatedOption = option?.map((data) => {
            if (data?.label) {
                return data
            }
            else {
                const isArithmeticOperator = /^[0-9+\-*/(){}[\]\s]+$/.test(data)
                if (isArithmeticOperator) {
                    return {
                        label: data,
                        value: data
                    }
                }
            }
        })
        const filteredOptions = updatedOption?.filter(element => (element !== undefined) && (element !== null));
        setHint(filteredOptions);
        setSelectedOptions(filteredOptions);
        setErrors({
            ...errors,
            customFormula: null,
        })
    }

    //----|| Generate Report ||----//
    const handleGenerateReport = (e) => {
        e.preventDefault();

        const type = selectedDataType?.value;
        const metricValue = selectedFormula?.value;
        const fieldsValue = fieldSelectOption?.map((field) => field.value);
        let customFormulae = "";
        hint?.map((field) => {
            return customFormulae += `${field?.value} `;
        })

        let currenErrors = {};
        if (!reportName && !reportName?.trim()) {
            currenErrors.reportName = "Formula name is required";
        }
        if (!type && !type?.trim()) {
            currenErrors.dataType = "Data type is required";
        }
        if (!metricValue && !metricValue?.trim()) {
            currenErrors.metrics = "Metric  is required";
        }
        if (!fieldsValue || fieldsValue?.length === 0) {
            currenErrors.fieldSelect = "Fields  are required";
        }
        if (isCustom) {
            if (!customFormulae && !customFormulae?.trim()) {
                currenErrors.customFormula = "Custom formula is required";
            }
            else {
                const isValid = validateExpression(customFormulae);
                if (!isValid) {
                    currenErrors.customFormula = "Custom formula is invalid";
                }
            }
        }
        setErrors(currenErrors);
        if (Object.keys(currenErrors).length === 0) {
            const dataToSend = {
                id: formulaDetails?.id,
                reportName: reportName,
                dataType: type,
                fields: fieldsValue,
                metrics: metricValue,
                isCustomFormula: isCustom,
                customFormula: isCustom ? customFormulae : null,
            }
            props.formulaEdit(dataToSend, filters, paginationData, parent);
        }
    }

    const handleCancel = (e) => {
        e.preventDefault();
        onClose();
        props.clearFormFields();
        props.clearFormulaDetails();
    }

    const validateClose = (e, reason) => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    }

    return (
        <>
            <Dialog
                open={open}
                TransitionComponent={Transition}
                maxWidth="lg"
                fullWidth
                keepMounted
                onClose={validateClose}
            >
                {open && (
                    <>
                        <Card sx={{ overflow: 'auto' }} >
                            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "center", pt: 0, pb: 0 }}>
                                {fromName === "form-configuration" ? "Formula Info" : "Edit Formula"}
                                <IconButton
                                    aria-label="close"
                                    onClick={onClose}
                                >
                                    <IconX />
                                </IconButton>
                            </DialogTitle>
                            <Divider />
                            <DialogContent>
                                {errorMessage && <ErrorAlert content={errorMessage} />}
                                <form>
                                    <Grid container paddingBottom={2} gap={4}>
                                        <Grid item lg={5} md={5}>
                                            <Grid container rowGap={2}>
                                                <Grid item lg={12} md={12}>
                                                    <Typography
                                                        variant="h4"
                                                        color="primary"
                                                        sx={{ ...headerStyles }}
                                                    >
                                                        <IconFileText color={theme.palette.secondary.dark} />
                                                        Formula Name
                                                    </Typography>
                                                </Grid>
                                                <Grid item lg={10}>
                                                    {fromName === "form-configuration" ? <Chip label={reportName} color="primary" sx={{ fontWeight: "bold" }} />
                                                        : <TextField
                                                            autoFocus
                                                            size="small"
                                                            label={<>Enter Formula Name <Required /></>}
                                                            fullWidth
                                                            onChange={handleReportName}
                                                            value={reportName}
                                                            error={errors?.reportName}
                                                            helperText={errors?.reportName}
                                                        />}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item lg={5} md={5}>
                                            <Grid container rowGap={2}>
                                                <Grid item lg={12} md={12}>
                                                    <Typography
                                                        variant="h4"
                                                        color="primary"
                                                        sx={{ ...headerStyles }}
                                                    >
                                                        <IconDatabase color={theme.palette.secondary.dark} />
                                                        Data Type
                                                    </Typography>
                                                </Grid>
                                                <Grid item lg={6} md={6}>
                                                    {fromName === "form-configuration" ? <Chip label={selectedDataType?.label} color="primary" sx={{ fontWeight: "bold" }} /> :
                                                        <Autocomplete
                                                            options={DATA_TYPE_OPTIONS}
                                                            onChange={handleDataTypeSelect}
                                                            value={selectedDataType}
                                                            size="small"
                                                            isOptionEqualToValue={(option, value) => {
                                                                return option?.label === value?.label || option?.value === value?.value;
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={<>Data Type <Required /></>}
                                                                    fullWidth
                                                                    error={errors?.dataType}
                                                                    helperText={errors?.dataType}
                                                                />
                                                            )}
                                                        />
                                                    }
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Divider />
                                    <Grid container rowGap={1} paddingBottom={2}>
                                        <Grid item lg={12} >
                                            <Typography
                                                variant="h4"
                                                color="primary"
                                                sx={{ ...headerStyles }}
                                            >
                                                <IconInputCheck color={theme.palette.secondary.dark} />
                                                Field Selection
                                                {fromName !== "form-configuration" &&
                                                    <>  <IconMinusVertical />
                                                        <FormGroup>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        onChange={handleSelectAll}
                                                                        checked={selectAll}
                                                                        disabled={formFieldsCount === 0}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography
                                                                        variant="h5"
                                                                        sx={{ fontWeight: "bold" }}
                                                                        color={formFieldsCount === 0 ? "grey" : "primary"}
                                                                    >
                                                                        Select All
                                                                    </Typography>
                                                                }
                                                            />
                                                        </FormGroup>
                                                    </>
                                                }
                                            </Typography>
                                        </Grid>

                                        {errors?.fieldSelect && <Grid item lg={12} paddingBottom={0}>
                                            <ErrorAlert content={errors?.fieldSelect} />
                                        </Grid>
                                        }
                                        {formFields?.length > 0 ?
                                            formFields?.map((field, index) => (
                                                fromName === "form-configuration" ? fieldsData[field.FieldName] && <Chip label={field.FieldName} color="primary" sx={{ fontWeight: "bold", mr: 1 }} /> :
                                                    <Grid item lg={4} key={index}>
                                                        <FormGroup>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        name={field.FieldName}
                                                                        onChange={handleSelectField}
                                                                        checked={fieldsData[field.FieldName] || false}
                                                                    />
                                                                }
                                                                label={field.FieldName}
                                                            />
                                                        </FormGroup>
                                                    </Grid>
                                            )) :
                                            <Typography variant="h5">
                                                Please select data type above.
                                            </Typography>
                                        }
                                    </Grid>
                                    <Divider />
                                    <Grid container rowGap={2} paddingBottom={2} direction="row" columnGap={3} paddingTop={1}>
                                        <Grid item lg={12}>
                                            <Typography
                                                variant="h4"
                                                color="primary"
                                                sx={{ ...headerStyles }}
                                            >
                                                <IconGeometry color={theme.palette.secondary.dark} />
                                                Metrics
                                            </Typography>
                                        </Grid>
                                        <Grid item lg={3}>
                                            {fromName === "form-configuration" ?
                                                <Chip label={selectedFormula?.label} color="primary" sx={{ fontWeight: "bold" }} /> :
                                                <Autocomplete
                                                    options={selectedDataType ? METRICS_OPTIONS : []}
                                                    onChange={handleSelectFormula}
                                                    value={selectedFormula}
                                                    noOptionsText="Select data type"
                                                    isOptionEqualToValue={(option, value) => {
                                                        return option?.label === value?.label || option?.value === value?.value;
                                                    }}
                                                    getOptionDisabled={(option) =>
                                                        selectedDataType?.value === "Yes/No" ?
                                                            option.label === "Sum" || option.label === "Custom"
                                                            : null
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={<>Metric <Required /></>}
                                                            size="small"
                                                            error={errors?.metrics}
                                                            helperText={errors?.metrics} />
                                                    )}
                                                />
                                            }
                                        </Grid>
                                        {isCustom &&
                                            <Grid item lg={8}>
                                                {fromName === "form-configuration" ?
                                                    <Chip label={hint?.map((i) => (<>{i.label}&nbsp;</>))} color="primary" sx={{ fontWeight: "bold" }} /> :
                                                    <Autocomplete
                                                        multiple
                                                        options={selectedFormula ? fieldSelectOption : []}
                                                        value={selectedOptions}
                                                        freeSolo
                                                        onChange={handleFormulaSelect}
                                                        noOptionsText="Please select field"
                                                        isOptionEqualToValue={(option, value) => {
                                                            // const regex = /^[0-9+\-*/(){}[\]\s]+$/;
                                                            // const bool = regex.test(value?.value) ? false : option?.value === value?.value;
                                                            // return bool;
                                                            option?.label === value
                                                        }}
                                                        filterOptions={(options, params) => {
                                                            const filtered = filter(options, params);
                                                            const { inputValue } = params || {};
                                                            const isArithmeticOperator = /^[0-9+\-*/(){}[\]\s]+$/.test(inputValue);
                                                            const isExisting = options.some((option) => inputValue === option.label);
                                                            if (inputValue !== '' && !isExisting && isArithmeticOperator && isCustom) {
                                                                filtered.push({
                                                                    inputValue,
                                                                    label: `${inputValue}`,
                                                                    value: inputValue,
                                                                });
                                                            }

                                                            return filtered;
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={<>Custom Formula <Required /></>}
                                                                size="small"
                                                                error={errors?.customFormula}
                                                                helperText={hint?.length > 0 ?
                                                                    <>
                                                                        Preview: {hint?.map((i) => (<>{i.label}&nbsp;</>))}
                                                                        {errors?.customFormula && <><br />Error: {errors?.customFormula}</>}
                                                                    </>
                                                                    : errors?.customFormula
                                                                }
                                                            />
                                                        )}
                                                    />
                                                }
                                            </Grid>
                                        }
                                    </Grid>
                                    <Divider />
                                    {fromName !== "form-configuration" &&
                                        <Grid container justifyContent="right" marginTop={2}>
                                            <Button
                                                variant="contained"
                                                onClick={handleGenerateReport}
                                                size="small"
                                                color="primary"
                                                disabled={isPending}
                                            >
                                                {isPending ? <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <CircularProgress size={'20px'} color='inherit' />
                                                    Loading...
                                                </div>
                                                    : 'Submit'
                                                }
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={handleCancel}
                                                sx={{ ml: 2 }}
                                                color='error'
                                                size="small"
                                            >
                                                Cancel
                                            </Button>
                                        </Grid>
                                    }
                                </form>
                            </DialogContent>
                        </Card>
                    </>
                )}
            </Dialog >
        </>
    )
}

const mapStateToProps = (state) => ({
    formulaDetails: state.formulae.formulaDetails,
    formFields: state.formulae.formFields,
    formFieldsCount: state.formulae.formFieldsCount,
    isPending: state.formulae.isFormulaEditPending,
    errorMessage: state.formulae.formulaEditErrorMessage,
})
const mapDispatchToProps = (dispatch) => ({
    getFormfields: (type) => dispatch(getFormfields(type)),
    clearFormFields: () => dispatch(clearFormFields()),
    formulaEdit: (dataToSend, filter, paginationData, from) => dispatch(formulaEdit(dataToSend, filter, paginationData, from)),
    clearFormulaDetails: () => dispatch(clearFormulaDetails()),
    formulaEditErrorClear: () => dispatch(formulaEditErrorClear()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ReportEdit);